/**
 * @module app.ext.mcg-informed
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions, Sorting, SortDirection, SortEntry } from 'app/shared/lib/essette-site';
import { RouteConfig, NavigationInstruction } from 'aurelia-router';
import { apiCall } from 'app/shared/lib/essette-api';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { AssessmentService } from 'app/core/services/assessment-service';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import { matcher } from 'app/shared/lib/matcher';

interface IAssessment {
    AssessmentID?: string | null,
    Description?: string | null,
    ExternalAssessmentIdentifier?: string | null,
    Edition?: string | null,
    Imported: string,
    Active?: boolean | null,
    RowSelectable: boolean
};

interface IMcgAssessment { Id: string, Title: string, Hsim: string, Edition: string };

@autoinject()
export class AssessmentManagement extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    protected assessments: Array<IAssessment> = [];
    protected importedAssessments: Array<CM.CaseAssessment> = [];
    protected mcgAssessments: Array<IMcgAssessment> = [];

    protected selectedAssessments: IAssessment[] = [];
    protected selectedCount: number = 0;

    protected showImportedAssessments: boolean = false;
    protected showInactiveAssessments: boolean = false;

    protected hasMcgAccess: boolean = false;

    protected assessmentsConfig: IAssessmentsConfig = {
        targetType: 'CM.Case',
        serviceRootUrl: 'cm/cases',
        backRouteName: 'cm.case.assessments'
    }

    private sorting: Sorting = new Sorting();
    private assessmentIdMatcher = (a: any, b: any): boolean => matcher(a, b, 'AssessmentID')

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<IAssessment>;

    ////****************** Lifecycle events (in order of execution) ******************////

    constructor(
        private readonly service: McgInformedService,
        private readonly assessmentService: AssessmentService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]
    ) {

        super(...rest);
    }

    protected async canActivate(params: any, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<boolean> {
        return await this.checkPermissions();
    }

    protected async activate(params: any, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        this.assessmentService.configure(this.assessmentsConfig);
        await this.loadAssessments(true);
    }

    ////****************** Events ***************************************************////

    protected assessmentsSelected({ detail }: SelectEvent<IAssessment>): void {
        const selectedAssessments: IAssessment[] = this.selectedAssessments.slice();
        // if detail is missing - a select all event occurred
        if(!detail.item) {
            // unselect all items
            if(detail.items.length === 0) {
                this.assessments.forEach(assessment => {
                    const index = selectedAssessments.findIndex(
                        selected => selected.AssessmentID === assessment.AssessmentID
                    );
                    if(index !== -1) {
                        selectedAssessments.splice(index, 1);
                    }
                });
            } else {
                // save all items
                detail.items.forEach(item => {
                    const alreadySelected = this.selectedAssessments.some(
                        assessment => assessment.AssessmentID === item.AssessmentID
                    );
                    if(!alreadySelected) {
                        selectedAssessments.push(item);
                    }
                });
            }
        } else {
            // save selected checkboxes to restore later logic
            const match = detail.items.some(
                item => item.AssessmentID === detail.item?.AssessmentID
            );
            // Check for matching id in detail (allows dups and id's correct service)
            if(match) {
                // add to selectedNotifications if not already present
                const alreadySelected = this.selectedAssessments.some(
                    assessment => assessment.AssessmentID === detail.item?.AssessmentID
                );
                if(!alreadySelected) {
                    selectedAssessments.push(detail.item!);
                }
            } else {
                // remove from selectedNotifications if it exists based on the id of detail
                const index = this.selectedAssessments.findIndex(
                    assessment => assessment.AssessmentID === detail.item?.AssessmentID
                );
                if(index !== -1) {
                    selectedAssessments.splice(index, 1);
                }
            }
        }

        this.selectedAssessments = selectedAssessments;
        this.selectedCount = this.selectedAssessments.length;
    }

    protected clearSelected(): void {
        if(this.$table) {
            this.$table.selected = [];
        }
        this.selectedAssessments = [];
        this.selectedCount = this.selectedAssessments.length;
    }

    private restoreSelected() {
        if(!(this.selectedAssessments && this.selectedAssessments.length > 0)) return;
        this.$table.restoreSelected(this.selectedAssessments.slice());
    }

    protected importSelectedAssessments(): void {
        if(this.selectedCount === 0) {
            const message: string = this.i18n.tr('ui:validation.oneSelected', { what: '$t(ext:cci.assessment.$)' });
            this.toaster.error().message(message).show();
            return;
        }

        this.importAssessments();
    }

    protected async toggleImportedAssessmentsDisplay(checked: boolean): Promise<void> {
        this.showImportedAssessments = checked;
        await this.loadAssessments();
    }

    protected async toggleInactiveAssessmentsDisplay(checked: boolean): Promise<void> {
        this.showInactiveAssessments = checked;
        await this.loadAssessments();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const sorting = this.sorting;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.loadAssessments();
    }

    ////****************** Functions ************************************************////

    private async checkPermissions(): Promise<boolean> {
        await this.accountContext.load();
        this.hasMcgAccess = this.env.config.integrations.includes('cci');
        return this.hasMcgAccess;
    }

    private async loadAssessments(resetSelections: boolean = false): Promise<void> {
        // make sure table does not retain any previous selections
        if(this.$table) this.$table.selected.splice(0);
        this.assessments = [];
        this.importedAssessments = [];
        this.mcgAssessments = [];
        if(resetSelections) {
            this.selectedAssessments = [];
            this.selectedCount = 0;
        }

        await this.processAssessments(this.showImportedAssessments, this.showInactiveAssessments);
        this.restoreSelected();
    }

    private async processAssessments(includeImported: boolean, includeInactive: boolean) {
        await this.loadImportedAssessments();
        await this.loadAssessmentList();

        this.mcgAssessments = this.mcgAssessments.filter(a => !this.importedAssessments.some(ia => ia.externalAssessmentIdentifier === a.Hsim && ia.currentVersion?.versionName === a.Edition));

        this.mcgAssessments.forEach(a => {
            this.assessments.push(
                {
                    "AssessmentID": a.Id,
                    "Description": a.Title,
                    "ExternalAssessmentIdentifier": a.Hsim,
                    "Edition": a.Edition,
                    "Imported": "No",
                    "Active": true,
                    "RowSelectable": true
                });
        });

        if(includeImported) {
            if(!includeInactive) { this.importedAssessments = this.importedAssessments.filter(a => a.isActive); }

            this.importedAssessments.forEach(a => {
                this.assessments.push({
                    "AssessmentID": a.id,
                    "Description": a.description,
                    "ExternalAssessmentIdentifier": a.externalAssessmentIdentifier,
                    "Edition": a.currentVersion?.versionName,
                    "Imported": "Yes",
                    "Active": a.isActive,
                    "RowSelectable": false
                });
            });
        }

        if(this.sorting.entries.length == 0) {
            this.assessments = await this.sort(this.assessments);
        }
        else {
            for(let i = 0; i < this.sorting.entries.length; i++) {
                let entry: SortEntry = this.sorting.entries[i];
                this.assessments = await this.sort(this.assessments, entry);
            }
        }
    }

    private async sort(assessments: IAssessment[], entry?: SortEntry): Promise<IAssessment[]> {
        assessments = assessments.sort(function(a, b) {
            if(entry !== undefined) {
                if(entry.reference == 'Description') {
                    let c = a.Description || '';
                    let d = b.Description || '';
                    return entry.direction == SortDirection.Descending ? d.localeCompare(c) : c.localeCompare(d);
                }
                else if(entry.reference == 'Imported') {
                    return entry.direction == SortDirection.Descending ? b.Imported.localeCompare(a.Imported) : a.Imported.localeCompare(b.Imported);
                }
                else if(entry.reference == 'Edition') {
                    let c = a.Edition || '';
                    let d = b.Edition || '';
                    return entry.direction == SortDirection.Descending ? d.localeCompare(c) : c.localeCompare(d);
                }
            }

            let c = a.Description || '';
            let d = b.Description || '';
            return c.localeCompare(d);
        });

        return assessments;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadAssessmentList(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadAssessmentList();
            this.mcgAssessments = <IMcgAssessment[]>(response.result.assessments);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadImportedAssessments(): Promise<void> {
        const options: StandardLoadOptions = {
            includeInactive: true
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadImportedAssessments(options);
            this.importedAssessments = <CM.CaseAssessment[]>(response.results);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async importAssessments(): Promise<void> {
        try {
            this.state.beginSave();

            for(const a of this.selectedAssessments) {
                const assessment: CM.CaseAssessment = {
                    description: a.Description,
                    externalAssessmentIdentifier: a.ExternalAssessmentIdentifier,
                    externalSystem: { code: 'CCI' },
                    targetType: 'CM.Case',
                    currentVersion: { versionName: a.Edition }
                };

                const assessmentResponse = await this.assessmentService.insertAssessment(assessment);

                if(assessmentResponse.isSuccessful && assessmentResponse.id) {
                    const assessmentId = assessmentResponse.id;
                    const externalContent = await this.loadExternalContent(assessmentId);
                    const attachment: Core.AssessmentAttachment = {
                        assessment: {
                            id: assessmentId
                        },
                        externalContent: {
                            content: externalContent,
                            externalContentType: { code: 'AssessmentJson' },
                            mediaType: { code: 'data.json' }
                        }
                    };

                    await this.createAssessmentObjects(attachment);
                }
            }

            this.toaster.standard.created('ext:cci.assessment.$', true, this.selectedAssessments.length);
            this.loadAssessments();
            this.clearSelected();
        }
        catch(error) {
            this.toaster.standard.created('ext:cci.assessment.$', false, this.selectedAssessments.length);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async createAssessmentObjects(attachment: Core.AssessmentAttachment): Promise<boolean> {
        try {
            const response = await this.service.createObjects(attachment);
            return response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadExternalContent(assessmentId: string): Promise<string | null> {
        try {
            const response = await this.service.loadAssessmentContent(assessmentId);
            if(!response.isSuccessful) return null;
            const externalContent = JSON.stringify(response.result);
            return externalContent;
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async activateAssessment(assessment: IAssessment, active: boolean): Promise<void> {
        if(!active) {
            const result = await confirmation.showDeactivate('ext:cci.assessment.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        this.assessmentService.configure(this.assessmentsConfig);

        const assessmentId: string = assessment.AssessmentID || '';

        try {
            this.state.beginSave();
            const response = await this.assessmentService.activateAssessment(assessmentId!, active);

            this.toaster.standard.activate('ext:cci.assessment.$', active, response.isSuccessful);

            if(response.isSuccessful) {
                this.loadAssessments();
            }
        } catch(error) {
            this.toaster.standard.activate('ext:cci.assessment.$', active, false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

}
